import { useEffect, useState } from "react";
import { GlobeHemisphereWest, Buildings } from "@phosphor-icons/react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatApiError } from "../../lib/utils-extra";
import { TIMEZONE_OPTIONS } from "../../lib/timezones";

/**
 * Canonical home for the organization default time zone (moved out of General).
 * Branch-level overrides remain edited on each Branch; here they are shown read-only.
 */
export function TimeZonesSettings() {
  const [settings, setSettings] = useState(null);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/branches").then(({ data }) => setBranches((data || []).filter((b) => b.active !== false))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Default time zone saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="py-8 text-center text-sm text-[#475569]">Loading…</div>;
  const orgTz = settings.default_timezone || "UTC";

  return (
    <div className="space-y-4" data-testid="time-zones-section">
      <div>
        <div className="font-display text-xl font-semibold flex items-center gap-2">
          <GlobeHemisphereWest size={20} weight="duotone" color="#0EA5E9" /> Time Zones
        </div>
        <p className="text-sm text-[#475569] mt-1">The organization-wide default time zone. Branches may override it on their own record.</p>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5 space-y-4">
          <div className="max-w-sm">
            <Label className="small-label">Organization default time zone</Label>
            <Select value={orgTz} onValueChange={(v) => setSettings({ ...settings, default_timezone: v })}>
              <SelectTrigger className="mt-1.5" data-testid="org-default-timezone"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {TIMEZONE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="save-timezone-button">
            {saving ? "Saving…" : "Save default time zone"}
          </Button>
        </CardContent>
      </Card>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <div className="font-display text-base font-semibold flex items-center gap-2 mb-1">
            <Buildings size={18} weight="duotone" color="#0EA5E9" /> Branch overrides
          </div>
          <p className="text-xs text-[#475569] mb-3">Read-only summary. Edit a branch&apos;s time zone on its record under Organization &rsaquo; Branches.</p>
          {branches.length === 0 ? (
            <div className="text-sm text-[#475569] py-4 text-center">No branches yet.</div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {branches.map((b) => (
                <div key={b.id} className="flex items-center justify-between py-2.5" data-testid={`branch-tz-${b.id}`}>
                  <span className="text-sm font-medium">{b.name}</span>
                  {b.timezone
                    ? <Badge className="bg-[#0EA5E9]/10 text-[#0EA5E9] border-0">{b.timezone}</Badge>
                    : <Badge className="bg-[#F1F5F9] text-[#475569] border-0">Org default · {orgTz}</Badge>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TimeZonesSettings;
